import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, Calculator, BarChart3, Building, 
  Briefcase, ArrowRight, CheckCircle, Landmark
} from 'lucide-react';

const Services = () => {
  const services = [
    {
      id: 'gst',
      icon: <FileText size={48} />,
      title: 'GST Services',
      description: 'Comprehensive GST compliance and advisory solutions',
      features: [
        'GST Registration',
        'GST Return Filing (GSTR-1, 3B, 9)',
        'GST Notice Handling',
        'Input Tax Credit Reconciliation',
        'GST Compliance Advisory'
      ],
      link: '/services/gst',
      color: '#1B5A7E'
    },
    {
      id: 'income-tax',
      icon: <Calculator size={48} />,
      title: 'Income Tax',
      description: 'Expert income tax services for individuals and businesses',
      features: [
        'ITR Filing (All Categories)',
        'Tax Planning & Optimization',
        'Scrutiny Assessment Handling',
        'Notice Representation',
        'Advance Tax Computation'
      ],
      link: '/services/income-tax',
      color: '#FF8C42'
    },
    {
      id: 'audit',
      icon: <BarChart3 size={48} />,
      title: 'Audit & Assurance',
      description: 'Professional audit services ensuring compliance and accuracy',
      features: [
        'Statutory Audit',
        'Tax Audit',
        'Internal Audit',
        'Management Audit',
        'Special Purpose Audit'
      ],
      link: '/services/audit',
      color: '#4CAF50'
    },
    {
      id: 'accounting',
      icon: <Building size={48} />,
      title: 'Accounting & Compliance',
      description: 'Complete accounting and regulatory compliance services',
      features: [
        'Bookkeeping & Accounting',
        'TDS Returns & Compliance',
        'Payroll Management',
        'ROC Compliance',
        'Annual Compliances'
      ],
      link: '/services/accounting',
      color: '#1B5A7E'
    },
    {
      id: 'advisory',
      icon: <Briefcase size={48} />,
      title: 'Business Advisory',
      description: 'Strategic business consulting and advisory services',
      features: [
        'Company/LLP Registration',
        'Partnership Firm Registration',
        'Financial Planning',
        'Loan & Project Reports',
        'Business Structuring'
      ],
      link: '/services/advisory',
      color: '#FF8C42'
    },
    {
      id: 'financial',
      icon: <Landmark size={48} />,
      title: 'Financial Services',
      description: 'Comprehensive loan and financing solutions for all needs',
      features: [
        'Business Loans',
        'Loan Against Property (LAP)',
        'Personal Loans',
        'Housing Loans',
        'Working Capital Finance'
      ],
      link: '/services/financial',
      color: '#4CAF50'
    }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-overlay"></div>
        <div className="container">
          <h1 className="page-hero-title">Our Services</h1>
          <p className="page-hero-subtitle">
            Comprehensive Financial Solutions for Your Business Growth
          </p>
        </div>
      </section>

      {/* Services Overview */}
      <section className="services-overview">
        <div className="container">
          <div className="services-intro">
            <h2 className="section-title-center">What We Offer</h2>
            <p className="services-intro-text">
              From GST compliance to strategic business advisory, we provide end-to-end 
              financial solutions tailored to meet the unique needs of individuals, startups, 
              and established enterprises.
            </p>
          </div>

          <div className="services-list">
            {services.map((service, index) => (
              <div key={service.id} className="service-detail-card">
                <div className="service-detail-header">
                  <div 
                    className="service-detail-icon"
                    style={{ backgroundColor: `${service.color}15`, color: service.color }}
                  >
                    {service.icon}
                  </div>
                  <div className="service-detail-title-section">
                    <h3 className="service-detail-title">{service.title}</h3>
                    <p className="service-detail-description">{service.description}</p>
                  </div>
                </div>

                <div className="service-detail-features">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="service-feature-item">
                      <CheckCircle size={18} className="feature-check-icon" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <Link to={service.link} className="service-detail-link">
                  Learn More <ArrowRight size={18} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <h2 className="section-title-center">Our Process</h2>
          <div className="process-grid">
            <div className="process-step">
              <div className="process-number">1</div>
              <h3 className="process-title">Consultation</h3>
              <p className="process-text">
                Understanding your needs and business requirements
              </p>
            </div>
            <div className="process-step">
              <div className="process-number">2</div>
              <h3 className="process-title">Analysis</h3>
              <p className="process-text">
                Detailed review of your financial situation and compliance status
              </p>
            </div>
            <div className="process-step">
              <div className="process-number">3</div>
              <h3 className="process-title">Implementation</h3>
              <p className="process-text">
                Execution of services with accuracy and timeliness
              </p>
            </div>
            <div className="process-step">
              <div className="process-number">4</div>
              <h3 className="process-title">Support</h3>
              <p className="process-text">
                Ongoing assistance and advisory for continued compliance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <div className="container">
          <div className="services-cta-content">
            <h2 className="services-cta-title">Need Professional Financial Services?</h2>
            <p className="services-cta-text">
              Get in touch with us to discuss how we can help your business thrive
            </p>
            <Link to="/contact" className="btn btn-primary">
              Schedule a Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
